import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit , signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { first, fromEvent, merge } from 'rxjs';
import { AccountService, SubscriptionType } from 'src/app/services/account/account.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { ImageProcessingService } from 'src/app/services/account/image-processing.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { ProfileDropdownComponent } from '../../profile-dropdown/profile-dropdown.component';
import { SearchComponent } from '../../search/search.component';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/models/user';

@Component({
  standalone: true,
  selector: 'app-header-panel',
  templateUrl: './header-panel.component.html',
  styleUrls: ['./header-panel.component.scss'],
  imports: [CommonModule, SearchComponent, ProfileDropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderPanelComponent implements OnInit {

  @Input() loadingMap?: string | null;

  public readonly isAuthorized = signal<boolean>(false);
  public readonly dropdownVisible = signal<boolean>(false);
  public readonly username = signal<string>('');
  public readonly avatarLink = signal<string>('');
  public readonly subscriptionType = signal<SubscriptionType | null>(null);
  public amazonS3BucketUrl: string = environment.AMAZON_S3_BUCKET_URL;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public accountService: AccountService,
    private cdr: ChangeDetectorRef,
    public settingsService: SettingsService,
    public languageService: LanguageService,
    private consoleLoggerService: ConsoleLoggerService,
    private utilsService: UtilsService,
    private imageProcessingService: ImageProcessingService
  ) { }

  ngOnInit(): void {
    this.accountService.ObservableIsAuthorized.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null && resp != undefined) {
        this.isAuthorized.set(resp);
        if (resp == false) {
          this.username.set('');
          this.dropdownVisible.set(false);
        }
      }
    });

    this.accountService.ObservableUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (resp: User | null) => {
      if (resp != null && resp != undefined && resp.Profile.FirstName) {
        this.username.set(resp.Profile.LastName
          ? resp.Profile.FirstName + ' ' + resp.Profile.LastName
          : resp.Profile.FirstName);
        if (this.accountService.getUserAvatar()) {
          this.avatarLink.set(await this.imageProcessingService.resizeImageUrlToSquare(
            `${this.amazonS3BucketUrl}/${this.accountService.getUserAvatar()}`, 48
          ));
        } else {
          this.avatarLink.set('');
        }
      }
    });


    this.accountService.ObservableSubscriptionType.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.subscriptionType.set(resp);
      }
    });
  }

  public profileClick(): void {
    if (this.dropdownVisible()) {
      this.dropdownVisible.set(false);
      return;
    }

    this.dropdownVisible.set(true);

    const clickEvent = fromEvent(document, 'click', { capture: true });
    const touchstartEvent = fromEvent(document, 'touchstart', { capture: true });

    merge(clickEvent, touchstartEvent).pipe(first()).subscribe((event: Event) => {
      if (!this.utilsService.isClassInParentChain(event, 'header-panel')) {
        this.dropdownVisible.set(false);
      }
    });
  }

}
